/* eslint-disable react/jsx-one-expression-per-line */
import React, { useEffect, useState, useMemo } from 'react';
import { Text, Box, Icon, Center, Heading, Container, Flex, Image } from '@chakra-ui/react';
import { FiBarChart, FiTrendingUp, FiLock, FiShield } from 'react-icons/fi';
import { useGlobal } from '../context/provider';
import { useQueryBuilder } from '../context/queryBuilderProvider';
import { OmniButton } from '../components/ui/buttons';
import faviconSvg from '../images/favicon.svg';

// TODO: Update for AWS Amplify v6 in Phase 5
// import { Auth, Hub } from 'aws-amplify';

// Mock user data interface
interface IAWSUser {
    username: string;
    attributes: {
        email: string;
    };
}

// Hub event payload interface
interface IHubPayload {
    event: string;
    data?: unknown;
}

// Hub listener callback interface
interface IHubListener {
    payload: IHubPayload;
}

// Props interface for LoginPage component
interface ILoginPageProps {
    onLogin?: () => void; // Function to call when user wants to sign in
    [key: string]: unknown; // Allow any additional props to be passed through
}

// TODO: Phase 5 - Replace with AWS Amplify v6 API
const Auth = {
    currentAuthenticatedUser: async (): Promise<IAWSUser> => ({
        username: 'test-user',
        attributes: { email: 'test@example.com' },
    }),
    signOut: async (): Promise<void> => { },
    federatedSignIn: async (): Promise<void> => { },
};

const Hub = {
    listen: (
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _channel: string,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _callback: (data: IHubListener) => void,
    ): void => { },
    remove: (
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _channel: string,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _callback: (data: IHubListener) => void,
    ): void => { },
};

const LoginPage: React.FC<ILoginPageProps> = ({ onLogin }) => {
    const from = useMemo(() => ({ pathname: '/nothing' }), []); // Mock location object
    const global = useGlobal();
    const signOut = useMemo(() => {
        return 'signOut' in global
            ? (global as unknown as { signOut: () => void }).signOut
            : () => console.log('signOut not available');
    }, [global]);
    const { userSignIn, signInFrom, signedInFrom } = useQueryBuilder();
    // const navigate = useNavigate();
    // const location = useLocation();
    // const { from } = location.state || { from: { pathname: '/' } };
    const [redirectToPreviousRoute, setRedirectToPreviousRoute] =
        useState<boolean>(false);

    // console.log('Hub location.state', location.state);

    function getUser(): Promise<IAWSUser | undefined> {
        return Auth.currentAuthenticatedUser()
            .then((userData: IAWSUser) => {
                console.log(userData);
                return userData;
            })
            .catch(() => {
                console.log('Not signed in');
                return undefined;
            });
    }

    useEffect(() => {
        let mounted = true;

        const hubListener = ({
            payload: { event, data: payloadData },
        }: IHubListener): void => {
            console.log('Hub listen', event, payloadData);
            switch (event) {
                case 'signIn':
                    console.log('Hub call sign in', from, signInFrom);
                    break;
                case 'cognitoHostedUI':
                    // setLoggedIn(true);
                    console.log('Hub call sign in CognitoHosted', from, signInFrom);
                    getUser()
                        .then((userData) => {
                            console.log('Hub history 1', from);
                            if (userData) {
                                // Note: userSignIn not called in mock environment
                                console.log('Mock user sign in for:', userData.username);
                            }
                            return userData;
                        })
                        .then((userData) => {
                            if (mounted && userData) {
                                console.log('Hub history 2', from);
                                console.log('Hub history 2', signInFrom);
                                // navigate(from);
                                // console.log('Hub history', navigate);
                                setRedirectToPreviousRoute(true);
                            }
                        });
                    break;
                case 'signOut':
                    signOut();
                    // setLoggedIn(false);
                    // setUser(null);
                    break;
                case 'oAuthSignOut':
                    console.log('Sign in oAuthSignOut', payloadData);
                    signOut();
                    // setLoggedIn(false);
                    // setUser(null);
                    break;
                case 'signIn_failure':
                case 'parsingCallbackUrl':
                    console.log('Hub   Sign in parsingCallbackUrl', payloadData);
                    // console.log('Sign in parsingCallbackUrl', payloadData);
                    // signOut();
                    // setLoggedIn(false);
                    // setUser(null);
                    break;
                case 'cognitoHostedUI_failure':
                    signOut();
                    // setLoggedIn(false);
                    console.log('Sign in failure', payloadData);
                    alert(`Sign in failure: ${payloadData}`);
                    break;
                default:
                    alert(`Unknown event failure: ${event}`);
            }
        };

        Hub.listen('auth', hubListener);

        return () => {
            mounted = false;
            // Cleanup function would be:
            // Hub.remove('auth', hubListener);
        };
    }, [from, signInFrom, userSignIn, signOut]);

    const login = (): void => {
        if (onLogin) {
            onLogin();
        } else {
            // Fallback to old method if no onLogin prop provided
            signedInFrom();
            console.log('Hub before auth', from);
            Auth.federatedSignIn();
            console.log('Hub after auth', from);
        }
    };

    console.log('Hub render', redirectToPreviousRoute, from);

    return (
        <Container maxW="100vw" minH="100vh" p={0}>
            {redirectToPreviousRoute ? (
                <Center minH="100vh" bg="linear-gradient(135deg, #3b3e3f 0%, #2c2f30 50%, #1e2021 100%)">
                    <Flex direction="column" align="center" gap={8} textAlign="center" color="white">
                        <Icon as={FiShield} boxSize={16} color="#00a1d2" />
                        <Heading size="lg" fontWeight="bold">Authentication Successful</Heading>
                        <Text fontSize="lg" color="#a2a9ad">
                            Redirecting you back to {from.pathname}...
                        </Text>
                        <Text fontSize="sm" color="#8a9499">
                            You came from: {signInFrom}
                        </Text>
                    </Flex>
                </Center>
            ) : (
                <Center
                    minH="100vh"
                    bgGradient="linear(135deg, #3b3e3f 0%, #5a5f61 25%, #7a8184 50%, #a2a9ad 75%, #f1f5fa 100%)"
                    position="relative"
                >
                    {/* Subtle OMNI brand accent overlay */}
                    <Box
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        bgGradient="linear(to-br, rgba(0, 161, 210, 0.05) 0%, rgba(3, 187, 227, 0.03) 50%, transparent 100%)"
                        pointerEvents="none"
                    />

                    <Container maxW="md" centerContent position="relative">
                        <Flex direction="column" align="center" gap={12} textAlign="center" color="#2d3748" w="full">

                            {/* OMNI Branding Header */}
                            <Flex direction="column" align="center" gap={4}>
                                <Box
                                    w={20}
                                    h={20}
                                    bg="linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)"
                                    borderRadius="xl"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    boxShadow="0 4px 20px rgba(59, 62, 63, 0.2)"
                                    border="1px solid rgba(162, 169, 173, 0.3)"
                                >
                                    <Image
                                        src={faviconSvg}
                                        alt="Nordic Data Explorer Logo"
                                        width="40px"
                                        height="40px"
                                    />
                                </Box>
                                <Heading
                                    size="xl"
                                    fontWeight="bold"
                                    letterSpacing="-0.02em"
                                    color="#2d3748"
                                    textShadow="0 1px 3px rgba(0,0,0,0.1)"
                                >
                                    Nordic Data Explorer
                                </Heading>
                                <Text fontSize="lg" color="#4a5568" maxW="sm">
                                    Professional analytics platform for Nordic markets
                                </Text>
                            </Flex>

                            {/* Feature Highlights */}
                            <Flex direction="column" align="center" gap={6} w="full">
                                <Flex direction="row" align="center" gap={4} w="full" justify="center">
                                    <Flex direction="column" align="center" gap={2} flex={1} maxW="120px">
                                        <Box
                                            p={3}
                                            bg="rgba(255, 255, 255, 0.8)"
                                            borderRadius="lg"
                                            backdropFilter="blur(10px)"
                                            border="1px solid rgba(162, 169, 173, 0.2)"
                                        >
                                            <Icon as={FiBarChart} boxSize={6} color="#00a1d2" />
                                        </Box>
                                        <Text fontSize="sm" color="#4a5568" textAlign="center">
                                            Advanced Analytics
                                        </Text>
                                    </Flex>

                                    <Flex direction="column" align="center" gap={2} flex={1} maxW="120px">
                                        <Box
                                            p={3}
                                            bg="rgba(255, 255, 255, 0.8)"
                                            borderRadius="lg"
                                            backdropFilter="blur(10px)"
                                            border="1px solid rgba(162, 169, 173, 0.2)"
                                        >
                                            <Icon as={FiTrendingUp} boxSize={6} color="#00a1d2" />
                                        </Box>
                                        <Text fontSize="sm" color="#4a5568" textAlign="center">
                                            Real-time Insights
                                        </Text>
                                    </Flex>

                                    <Flex direction="column" align="center" gap={2} flex={1} maxW="120px">
                                        <Box
                                            p={3}
                                            bg="rgba(255, 255, 255, 0.8)"
                                            borderRadius="lg"
                                            backdropFilter="blur(10px)"
                                            border="1px solid rgba(162, 169, 173, 0.2)"
                                        >
                                            <Icon as={FiShield} boxSize={6} color="#00a1d2" />
                                        </Box>
                                        <Text fontSize="sm" color="#4a5568" textAlign="center">
                                            Enterprise Security
                                        </Text>
                                    </Flex>
                                </Flex>
                            </Flex>

                            {/* Authentication Section */}
                            <Flex direction="column" align="center" gap={6} w="full">
                                <Box
                                    w="full"
                                    p={8}
                                    bg="rgba(255, 255, 255, 0.95)"
                                    borderRadius="xl"
                                    boxShadow="0 8px 32px rgba(59, 62, 63, 0.15)"
                                    backdropFilter="blur(10px)"
                                    border="1px solid rgba(162, 169, 173, 0.2)"
                                >
                                    <Flex direction="column" align="center" gap={6}>
                                        <Flex direction="column" align="center" gap={3} textAlign="center">
                                            <Icon as={FiLock} boxSize={8} color="#00a1d2" />
                                            <Heading size="md" color="gray.800" fontWeight="semibold">
                                                Secure Access Required
                                            </Heading>
                                            <Text fontSize="sm" color="gray.600" lineHeight="1.6">
                                                You need to authenticate to access the analytics dashboard at{' '}
                                                <Text as="span" fontWeight="semibold" color="#00a1d2">
                                                    {from.pathname}
                                                </Text>
                                            </Text>
                                        </Flex>

                                        <OmniButton
                                            size="lg"
                                            w="full"
                                            onClick={login}
                                            bg="#00a1d2"
                                            color="white"
                                            _hover={{
                                                bg: "#03bbe3",
                                                transform: 'translateY(-1px)',
                                                boxShadow: '0 4px 20px rgba(0, 161, 210, 0.4)'
                                            }}
                                            _active={{
                                                bg: "#007ca3",
                                                transform: 'translateY(0)'
                                            }}
                                            transition="all 0.2s ease"
                                        >
                                            🔐 Sign In with SSO
                                        </OmniButton>

                                        <Text fontSize="xs" color="gray.500" textAlign="center">
                                            Secured by AWS Cognito • GDPR Compliant
                                        </Text>
                                    </Flex>
                                </Box>
                            </Flex>

                            {/* Footer Info */}
                            <Flex direction="column" align="center" gap={2}>
                                <Text fontSize="sm" color="#4a5568">
                                    Denmark • Norway • Sweden
                                </Text>
                                <Text fontSize="xs" color="#718096">
                                    Powered by Omnicom Data Platform
                                </Text>
                            </Flex>

                        </Flex>
                    </Container>
                </Center>
            )}
        </Container>
    );
};

export default LoginPage;
